import { EXPORT_FORMAT_LABEL, EXPORT_FORMAT_ORDER, ExportFormat } from 'modules/pond/constants'
import { useTranslation } from 'react-i18next'

import { Box, CircularProgress, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface ExportFormatMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  onSelect: (format: ExportFormat) => void
  /** Format currently downloading, or null when idle. */
  downloadingFormat: ExportFormat | null
}

/** Short badge text per format, matching the file extension rather than the label. */
const FORMAT_BADGE: Record<ExportFormat, string> = {
  [ExportFormat.Pdf]: 'PDF',
  [ExportFormat.Xlsx]: 'XLS',
  [ExportFormat.Csv]: 'CSV',
}

/**
 * Popover that picks the file format for an export. One download runs at a time, so
 * the active row shows a spinner and the others are disabled while it is in flight.
 */
const ExportFormatMenu = ({
  anchorEl,
  onClose,
  onSelect,
  downloadingFormat,
}: ExportFormatMenuProps) => {
  const { t } = useTranslation('pond')
  const theme = useTheme()
  const isDownloading = downloadingFormat !== null

  const badgeColor: Record<ExportFormat, string> = {
    [ExportFormat.Pdf]: theme.palette.error.main,
    [ExportFormat.Xlsx]: theme.palette.success.main,
    [ExportFormat.Csv]: theme.palette.info.main,
  }

  const badgeBackground: Record<ExportFormat, string> = {
    [ExportFormat.Pdf]: theme.palette.error.lighter,
    [ExportFormat.Xlsx]: theme.palette.success.lighter,
    [ExportFormat.Csv]: theme.palette.info.lighter,
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      PaperProps={{
        sx: {
          minWidth: 280,
          borderRadius: 2,
          p: 1,
          boxShadow: theme.customShadows?.z20,
        },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
        {t('select.file.format')}
      </Typography>

      {EXPORT_FORMAT_ORDER.map((format) => {
        const isActive = downloadingFormat === format

        return (
          <MenuItem
            key={format}
            onClick={() => onSelect(format)}
            disabled={isDownloading}
            sx={{ borderRadius: 1.5, py: 1.5, px: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 36,
                  height: 28,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: badgeBackground[format],
                  color: badgeColor[format],
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {isActive ? <CircularProgress color="inherit" size={14} /> : FORMAT_BADGE[format]}
              </Box>
              <Typography variant="body1" fontWeight={500}>
                {EXPORT_FORMAT_LABEL[format]}
              </Typography>
            </Stack>
          </MenuItem>
        )
      })}
    </Menu>
  )
}

export default ExportFormatMenu
